/* eslint-disable no-plusplus */
/* eslint-disable consistent-return */
import React, { useEffect, useRef } from 'react';

interface FlowerAnimationArtProps {
  width?: number;
  height?: number;
  petals?: number;
  speed?: number;
}

export function FlowerAnimationArt({
  width = 400,
  height = 400,
  petals = 6,
  speed = 0.02,
}: FlowerAnimationArtProps): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = width / 2;
    const centerY = height / 2;
    const maxRadius = Math.min(width, height) / 3;

    const draw = () => {
      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Draw flower petals
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;

      for (let i = 0; i < petals; i++) {
        const baseAngle = (i / petals) * Math.PI * 2;
        const petalPoints: [number, number][] = [];

        // Generate petal shape using sine curves
        for (let t = 0; t <= Math.PI; t += 0.05) {
          const distortion = Math.sin(timeRef.current * speed + i) * 0.3;
          const radius = maxRadius * Math.sin(t) * (1 + distortion);

          const angle = baseAngle + (Math.cos(t) - 1) * 0.5;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;

          petalPoints.push([x, y]);
        }

        // Draw petal
        ctx.beginPath();
        petalPoints.forEach((point, index) => {
          if (index === 0) {
            ctx.moveTo(point[0], point[1]);
          } else {
            ctx.lineTo(point[0], point[1]);
          }
        });
        ctx.stroke();
      }

      // Draw center circle
      const centerRadius = maxRadius * 0.15;
      ctx.beginPath();
      ctx.arc(centerX, centerY, centerRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Draw pulsing circle in center
      const pulseRadius =
        centerRadius * 0.6 * (1 + Math.sin(timeRef.current * speed * 2) * 0.3);
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
      ctx.fill();

      timeRef.current += 1;
      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, petals, speed]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block' }}
    />
  );
}

export default FlowerAnimationArt;
